import React, { useEffect, useState } from "react";
import "./CaseSetup.scss";
import {
  MotifAccordion,
  MotifAccordionContent,
  MotifAccordionTrigger,
} from "@ey-xd/motif-react";
import ControlInputBox from "./Form/ControlInputBox";
import Button from "@/components/Form/Button/Button";
import { useForm } from "react-hook-form";
import { MotifErrorMessage } from "@ey-xd/motif-react";
import { useTranslation } from "react-i18next";
import { CaseSetupFormType } from "./Form/type";
import {
  CASE_DESCRIPTION_ID,
  CASE_DESCRIPTION_ID_F,
  CASE_ID,
  CASE_ID_F,
  CASE_SHORT_ID,
  CASE_TIME_ZONE,
  IS_FRENCH_SELECTED,
} from "./Constants";
import ConfirmationModal from "@/components/Modal/ConfirmationModal/ConfirmationModal";
import ControlCheckBox from "@/components/Form/CheckBox/ControlCheckBox";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { capitalizeWordsInSentence, getTimezones } from "@/utility/common";
import { RootState } from "@/app/store";
import {
  initialState,
  setCaseIdentifier,
  updateFormSubmitIndex,
  updateStage,
} from "./caseSetupSlice";
import ControlTextAreaWithCharLimit from "@/components/Form/InputBox/ControlTextAreaWithCharLimit";
import ControlSelect from "@/components/Form/SelectBox/ControlSelect";
import { useGetTimeZoneListQuery } from "../Configuration/CaseTimeZones/caseTimeZoneReducer";
import { setDiscardEditMode } from "@/components/Modal/ConfirmationModal/discardchanges";
import {
  CANCEL_BUTTON_VARIANT,
  NEXT_BUTTON_VARIANT,
} from "@/components/Images/CommonImages";
import ControlEmailTextBox from "@/components/Form/InputBox/ControlEmailTextBox";

export default function CaseSetup() {
  const [isVisible, setIsVisible] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(true);
  const [showError, setShowError] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const CaseSetupData = useSelector((state: RootState) => state.caseSetup);
  // const timeZoneList = useSelector(
  //   (state: RootState) => state.timezone
  // );
  const caseSetupForm = CaseSetupData.caseSetupForm;
  const formSubmitIndex = CaseSetupData.formSubmitIndex;

  const { data, isLoading } = useGetTimeZoneListQuery({});

  const [isFrenchSelected, setIsFrenchSelected] = useState(
    caseSetupForm.isFrenchSelected || false
  );

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    setError,
    setValue,
    formState: { errors, isDirty },
  } = useForm<CaseSetupFormType>({
    defaultValues: {
      caseName: caseSetupForm.caseName,
      caseDescription: caseSetupForm.caseDescription,
      caseDescriptionInFrench: caseSetupForm.caseDescriptionInFrench,
      caseShortName: caseSetupForm.caseShortName,
      caseNameInFrench: caseSetupForm.caseNameInFrench,
      status: caseSetupForm.status,
      isFrenchSelected: caseSetupForm.isFrenchSelected,
      caseTimeZone: caseSetupForm.caseTimeZone,
      notificationRecipients: caseSetupForm.notificationRecipients || "",
    },
  });
  const checkBoxHandler = (e?: React.ReactEventHandler) => {
    setIsFrenchSelected(!isFrenchSelected);
  };

  const cancelBtnApproveHandler = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    const { caseSetupForm } = initialState;
    reset({
      caseName: caseSetupForm.caseName,
      caseDescription: caseSetupForm.caseDescription,
      caseDescriptionInFrench: caseSetupForm.caseDescriptionInFrench,
      caseShortName: caseSetupForm.caseShortName,
      caseNameInFrench: caseSetupForm.caseNameInFrench,
      status: caseSetupForm.status,
      isFrenchSelected: caseSetupForm.isFrenchSelected,
      caseTimeZone: caseSetupForm.caseTimeZone,
    });
    dispatch(setCaseIdentifier(caseSetupForm));
    navigate("/");
  };

  const errorList = Object.keys(errors);
  let errorsShow = capitalizeWordsInSentence(errorList.join(", "));

  const onSubmit = (formData: CaseSetupFormType) => {
    if (!formData.caseName || formData.caseName.trim() === "") {
      setShowError(true);
      return;
    }
    let caseOWnerDetails = formData.notificationRecipients;
    if (caseOWnerDetails?.[caseOWnerDetails?.length - 1] === ";") {
      caseOWnerDetails = caseOWnerDetails.slice(0, -1); // Remove the last semicolon if it exists
    }
    formData.notificationRecipients = caseOWnerDetails;
    const caseTimeZoneName = timeZones.find(
      (timeZone) => Number(timeZone.value) === Number(formData.caseTimeZone)
    )?.label;
    dispatch(setCaseIdentifier({ ...formData, caseTimeZoneName }));
    dispatch(updateStage(1));
    formSubmitIndex !== 3 && dispatch(updateFormSubmitIndex(0));
  };
  useEffect(() => {
    dispatch(setDiscardEditMode({ editMode: isDirty }));
    return () => {
      dispatch(setDiscardEditMode({ editMode: false }));
    };
  }, [dispatch, isDirty]);

  useEffect(() => {
    if (showError) {
      setTimeout(() => {
        setShowError(false);
      }, 5000);
    }
  }, [showError]);
  if (isLoading) {
    return <div>{t("global.globalNotifications.loadingMessage")}</div>;
  }
  const defaultOption = [
    { label: t("global.globalActions.optionDefault"), value: "" },
  ];

  const timeZones = getTimezones(data, defaultOption);

  return (
    <>
      <div className="motif-container pad-l-40 pad-r-20">
        <div className="motif-row">
          <div className="motif-col-lg-12  main-container">
            <div className="motif-row t-b-padding-21">
              <div className="motif-col-xs-4">
                <p className="form-title">
                  {t("pages.caseSetup.caseDetails.title")}
                </p>
                <p className="form-description">
                  {t("pages.caseSetup.caseDetails.description")}
                </p>
              </div>
            </div>
            <div className="motif-row t-b-padding-21">
              <div className="motif-col-xs-4">
                <div
                  style={{
                    alignItems: "flex-start",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "15px",
                  }}
                >
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <MotifAccordion
                      onClose={() => setIsFormOpen(false)}
                      onOpen={() => setIsFormOpen(true)}
                      style={{
                        flex: "1",
                      }}
                      open={isFormOpen}
                      useChevronIcon={true}
                      alignIconRight={true}
                    >
                      <MotifAccordionTrigger>
                        {t(
                          "pages.caseSetup.caseDetails.caseIdentifiersEnglish"
                        )}
                      </MotifAccordionTrigger>
                      <MotifAccordionContent>
                        <div className="motif-row">
                          <div className="motif-col-xl-6">
                            <div className="form-field">
                              <ControlInputBox
                                name={CASE_ID}
                                control={control}
                                required={true}
                                isTextArea={false}
                                label={
                                  "pages.caseSetup.caseDetails.formLabels.caseName"
                                }
                                showLabel={true}
                              />
                            </div>
                            <div className="form-field pad-t-10">
                              <ControlInputBox
                                name={CASE_SHORT_ID}
                                control={control}
                                required={true}
                                label={
                                  "pages.caseSetup.caseDetails.formLabels.shortName"
                                }
                                showLabel={true}
                              />
                            </div>
                          </div>
                          <div className="motif-col-xl-6">
                            <div className="form-field case-description-field">
                              {/* <ControlInputBox name={CASE_DESCRIPTION_ID} control={control} required={true} isTextArea={true} label={t(CASE_DESCRIPTION)} showLabel={true} /> */}
                              <ControlTextAreaWithCharLimit
                                name={CASE_DESCRIPTION_ID}
                                control={control}
                                required={true}
                                isTextArea={true}
                                label={
                                  "pages.caseSetup.caseDetails.formLabels.caseDescription"
                                }
                                showLabel={true}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="motif-row next-row pad-t-10">
                          <div className="motif-col-md-12">
                            <div className="form-field">
                              <ControlCheckBox
                                id="checkbox-french-selection"
                                onChange={checkBoxHandler}
                                checked={isFrenchSelected}
                                value={isFrenchSelected}
                                name={IS_FRENCH_SELECTED}
                                control={control}
                                required={false}
                                label={t(
                                  "pages.caseSetup.caseDetails.formLabels.includeInFrench"
                                )}
                                setIsFrenchSelected={setIsFrenchSelected}
                              />
                            </div>
                          </div>
                        </div>
                        <div
                          className={`motif-row mar-t-20 ${
                            !isFrenchSelected ? "hide" : ""
                          }`}
                        >
                          <div className="motif-col-md-4">
                            <div className="form-field">
                              <ControlInputBox
                                name={CASE_ID_F}
                                control={control}
                                required={isFrenchSelected ? true : false}
                                isTextArea={false}
                                label={
                                  "pages.caseSetup.caseDetails.formLabels.caseNameFrench"
                                }
                                showLabel={true}
                              />
                            </div>
                          </div>
                          <div className="motif-col-md-4">
                            <div className="form-field">
                              {/* <ControlInputBox name={CASE_DESCRIPTION_ID_F} control={control} required={isFrenchSelected ? true : false}  isTextArea={true} label={t(`${CASE_DESCRIPTION} (${FRENCH})`)} showLabel={true} /> */}
                              <ControlTextAreaWithCharLimit
                                name={CASE_DESCRIPTION_ID_F}
                                control={control}
                                required={isFrenchSelected ? true : false}
                                isTextArea={true}
                                label={
                                  "pages.caseSetup.caseDetails.formLabels.caseDescriptionFrench"
                                }
                                showLabel={true}
                              />
                            </div>
                          </div>
                        </div>
                      </MotifAccordionContent>
                    </MotifAccordion>
                    <MotifAccordion
                      onClose={() => setIsFormOpen(false)}
                      onOpen={() => setIsFormOpen(true)}
                      style={{
                        flex: "1",
                      }}
                      open={isFormOpen}
                      useChevronIcon={true}
                      alignIconRight={true}
                    >
                      <MotifAccordionTrigger>
                        {t(
                          "pages.caseSetup.caseDetails.formLabels.caseConfiguration"
                        )}
                      </MotifAccordionTrigger>
                      <MotifAccordionContent>
                        <div className="motif-row">
                          <div className="motif-col-xl-6">
                            <div className="form-field">
                              <ControlSelect
                                name={CASE_TIME_ZONE}
                                control={control}
                                required={true}
                                label={t(
                                  "pages.caseSetup.caseDetails.formLabels.timeZone"
                                )}
                                options={timeZones}
                                showLabel={true}
                                getValues={getValues}
                                setValue={setValue}
                                filter={true}
                              />
                            </div>
                          </div>
                          <div className="motif-col-xl-6">
                            <div className="form-field">
                              <ControlEmailTextBox
                                setError={setError}
                                name={"notificationRecipients"}
                                control={control}
                                required={true}
                                label={"pages.caseSetup.caseDetails.formLabels.notificationRecipients"}
                                showLabel={true}
                              />
                            </div>
                          </div>
                        </div>
                      </MotifAccordionContent>
                    </MotifAccordion>

                    <div className="error-container">
                      <MotifErrorMessage>
                        {errorList.length > 0 && !isVisible
                          ? t("pages.caseSetup.caseDetails.validationMessage", {
                              filedNames: errorsShow,
                            })
                          : ""}
                      </MotifErrorMessage>
                      <MotifErrorMessage>
                        {showError &&
                          t("pages.caseSetup.caseDetails.caseNameValidation")}
                      </MotifErrorMessage>
                    </div>
                    <div className="button-container sticky-footer">
                      <Button
                        variant={NEXT_BUTTON_VARIANT}
                        label={t("global.globalActions.next")}
                      />
                      <Button
                        type="button"
                        label={t("global.globalActions.cancel")}
                        variant={CANCEL_BUTTON_VARIANT}
                        className="cancelBtn"
                        onClickHandler={(e) => {
                          if (isDirty) {
                            setIsVisible(true);
                            dispatch(setDiscardEditMode({ editMode: false }));
                          } else {
                            cancelBtnApproveHandler(e);
                          }
                        }}
                      />
                    </div>
                  </form>
                  <ConfirmationModal
                    isVisible={isVisible}
                    setIsVisible={setIsVisible}
                    cancelBtnApproveHandler={cancelBtnApproveHandler}
                    cancelBtnRejectHandler={() => {
                      setIsVisible(false);
                      dispatch(setDiscardEditMode({ editMode: true }));
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
